import { AiUploadedDocument } from './AiUploadDocumentSection'
import { UploadedAttachmentState } from './aiDataExtractionRequest'
import { MAX_DOCUMENT_FILE_SIZE_BYTES } from './aiUploadedAttachmentsConstants'
import { asAttachmentUploadState, createUploadedDocument, revokeDocumentPreviewUrl } from './aiUploadedAttachmentsPure'

export type SelectFirstEligibleDocumentOptions = {
  currentDocument: AiUploadedDocument | null
  fileListFromInput: FileList | null
  maxFileSizeErrorMessage: string
  onDocumentChosen?: (document: AiUploadedDocument) => void
  pickEligibleFile: (fileListFromInput: FileList) => File | undefined
  setDocument: (document: AiUploadedDocument | null) => void
  setDocumentsMaxFileSizeErrorText: (errorText: string | undefined) => void
  setErrorText: (errorText: string | undefined) => void
  setUploadedAttachment: (attachmentState: UploadedAttachmentState | null) => void
  startUpload: (file: File, selectionDocumentId: string) => void
  unsupportedFileTypeErrorMessage: string
}

/** Single-file document flow: pick one allowed file, enforce max size, replace selection, then run startUpload (e.g. multimedia mirror). */
export const selectFirstEligibleDocumentAndStartUpload = ({
  currentDocument,
  fileListFromInput,
  maxFileSizeErrorMessage,
  onDocumentChosen,
  pickEligibleFile,
  setDocument,
  setDocumentsMaxFileSizeErrorText,
  setErrorText,
  setUploadedAttachment,
  startUpload,
  unsupportedFileTypeErrorMessage,
}: SelectFirstEligibleDocumentOptions) => {
  if (!fileListFromInput) {
    return
  }

  const eligibleFile = pickEligibleFile(fileListFromInput)

  if (!eligibleFile) {
    if (fileListFromInput.length > 0) {
      setDocumentsMaxFileSizeErrorText(undefined)
      setErrorText(unsupportedFileTypeErrorMessage)
    }
    return
  }

  if (eligibleFile.size > MAX_DOCUMENT_FILE_SIZE_BYTES) {
    setErrorText(undefined)
    setDocumentsMaxFileSizeErrorText(maxFileSizeErrorMessage)
    return
  }

  const newDocument = createUploadedDocument(eligibleFile)
  revokeDocumentPreviewUrl(currentDocument)
  setErrorText(undefined)
  setDocumentsMaxFileSizeErrorText(undefined)
  setUploadedAttachment(asAttachmentUploadState('uploading'))
  setDocument(newDocument)
  onDocumentChosen?.(newDocument)
  startUpload(eligibleFile, newDocument.id)
}

export const clearDocumentSelectionAndRevokePreview = (
  currentDocument: AiUploadedDocument | null,
  setDocument: (document: AiUploadedDocument | null) => void,
  setUploadedAttachment: (attachmentState: UploadedAttachmentState | null) => void,
  syncDocumentRefToNull?: () => void,
  clearSlotAndSectionErrors?: () => void
) => {
  revokeDocumentPreviewUrl(currentDocument)
  syncDocumentRefToNull?.()
  setDocument(null)
  setUploadedAttachment(null)
  clearSlotAndSectionErrors?.()
}
